import { Link, Spinner, SpinnerSize, IconButton, TooltipHost, mergeStyles } from '@fluentui/react';
import * as React from 'react';
import { AutomationIds } from '../../../models/constants/automationIds';
import { IconNames } from '../../../models/constants/constants';
import { AttachmentsConfig } from '../../../models/reduxState';
import { getAttachmentsClassNames, IAttachmentsStyles, iconStyle } from '../../contactUs/styles/attachmentsStyles';
import { AttachmentData, ErrorCodes, Attachment } from '../models/requestModels';
import { getCommonClassNames, getCommonStyles } from '../styles/commonStyles';
import { getRequestDetailsClassNames, getRequestDetailsStyles } from '../styles/requestDetailsStyles';
import { IStyledComponent } from '../styles/themesUI';
import { getString } from '../../../../web/utils/stringLoader';

interface IAttachmentsStateProps {
  attachmentsConfig: AttachmentsConfig;
  isUploadAttachmentInProgress: boolean;
  attachmentError: ErrorCodes;
  readOnlyMode?: boolean;
  dispatchGetUploadAttachmentToken: (
    workspaceId: string,
    requestId: string,
    chunkSizeInMB: number,
    doc: File
  ) => void;
  addAttachmentAction: (name: string, requestId?: string) => void;
  deleteAttachmentAction: (cwsId: string, requestId?: string, workspaceId?: string) => void;
  dispatchGetDownloadAttachmentToken: (cwsId: string, requestId: string) => void;
  dispatchCancelUploadAttachment: (requestId: string) => void;
  setAttachmentError: (error: ErrorCodes) => void;
}

interface IRequestAttachmentsProps {
  workspaceId: string;
  requestId: string;
  attachments: AttachmentData[];
}

type IAttachmentsProps = IAttachmentsStateProps & IRequestAttachmentsProps & IStyledComponent<IAttachmentsStyles>;

export class Attachments extends React.Component<IAttachmentsProps> {
  private browseInputRef = React.createRef<HTMLInputElement>();

  public render() {
    const attachments = this.mapAttachmentDataToAttachments();
    const maxAttachmentsCount = this.props.attachmentsConfig.MaxAttachmentsCount;
    const attachmentsAvailable = maxAttachmentsCount - (this.props.attachments ? this.props.attachments.length : 0);
    const maxSize = this.props.attachmentsConfig.MaxFileSizeInMB / 1024 + 'GB';
    const attachmentsClassNames = getAttachmentsClassNames(this.props.styles, { theme: this.props.theme });
    const requestDetailsClassNames = getRequestDetailsClassNames(
      getRequestDetailsStyles,
      { theme: this.props.theme }
    );
    const commonClassNames = getCommonClassNames(getCommonStyles);
    let uploadInstructionText: string = null;
    if (getString('attachments_uploadInstructionText', true, true, true)) {
      uploadInstructionText = getString('attachments_uploadInstructionText').replace('{0}', `${maxAttachmentsCount}`).replace('{1}', `${maxSize}`);
    }
    let remainingFilesText: string = null;
    if (getString('attachments_remainingFilesText', true, true, true)) {
      remainingFilesText = getString('attachments_remainingFilesText').replace('{0}', `${attachmentsAvailable}`);
    }
    let allowedExtns: string = null;
    if (this.props.attachmentsConfig.AllowedFileExtensions) {
      allowedExtns = this.props.attachmentsConfig.AllowedFileExtensions.toString()?.replace(/,/gi, ',.');
    }

    return (
      <>
        <div
          data-automation-id={AutomationIds.Attachments}
          className={requestDetailsClassNames.sectionContainer}
        >
          <div className='attachments-title-container'>
            {/* Display attachments label on read-only mode only if there are attachments to view/download on the ticket */}
            {(!this.props.readOnlyMode || (this.hasAttachments() && this.props.readOnlyMode)) &&
              <div
                className={mergeStyles(
                  commonClassNames.fontWeightBold,
                  requestDetailsClassNames.contentContainer
                )}
              >
                {getString('attachments_title')}
              </div>
            }
            {!this.props.readOnlyMode &&
              <div
                tabIndex={0}
                className={attachmentsClassNames.informationText}
              >
                {uploadInstructionText}
                <br></br>
                {remainingFilesText}
              </div>
            }
          </div>
          <div>
            {this.hasAttachments() &&
              attachments.map((attachment: Attachment, index: number) => {
                const calloutProps = { gapSpace: 0 };
                const tooltipId = `tooltip_${index}`;
                return (
                  <div
                    className={attachmentsClassNames.container}
                    id={attachment.AttachmentData.CwsId}
                    key={attachment.AttachmentData.CwsId}
                  >
                    <div
                      data-automation-id={AutomationIds.RequestAttachmentName}
                      className={attachmentsClassNames.attachmentContainer}
                    >
                      <TooltipHost
                        content={attachment.AttachmentData.Name}
                        id={tooltipId}
                        calloutProps={calloutProps}
                      >
                        <p
                          aria-describedby={tooltipId}
                          className={attachmentsClassNames.name}
                        >
                          {attachment.AttachmentData.Name}
                        </p>
                      </TooltipHost>
                    </div>
                    <div className={attachmentsClassNames.iconContainer}>
                      {
                        // Spinner and Cancel attachment icons not required if in read-only mode
                        (attachment.IsUploadAttachmentInProgress && this.props.isUploadAttachmentInProgress && !this.props.readOnlyMode)
                          ? (
                            <>
                              <Spinner
                                size={SpinnerSize.medium}
                                className={attachmentsClassNames.spinnerIconButton}
                              />
                              <IconButton
                                data-automation-id={AutomationIds.RequestCancelUploadAttachment}
                                className={attachmentsClassNames.cancelIconButton}
                                iconProps={{ iconName: IconNames.CancelIcon }}
                                title={attachment.AttachmentData.Name + ' ' + getString('attachments_cancelAFileText')}
                                onClick={this.onCancelButtonClicked}
                                styles={iconStyle}
                              />
                            </>
                          )
                          : (
                            <>
                              <IconButton
                                data-automation-id={AutomationIds.RequestDownloadAttachment}
                                className={attachmentsClassNames.downloadIconButton}
                                iconProps={{ iconName: IconNames.DownloadIcon }}
                                title={attachment.AttachmentData.Name + ' ' +getString('attachments_downloadAFileText')}
                                onClick={this.onDownloadButtonClicked(attachment.AttachmentData.CwsId)}
                                styles={iconStyle}
                              />
                              {!this.props.readOnlyMode &&
                                <IconButton
                                  data-automation-id={AutomationIds.RequestDeleteAttachment}
                                  className={attachmentsClassNames.deleteIconButton}
                                  iconProps={{ iconName: IconNames.CancelIcon }}
                                  title={attachment.AttachmentData.Name + ' ' + getString('attachments_deleteAFileText')}
                                  onClick={() => this.onDeleteButtonClicked(attachment.AttachmentData.CwsId)}
                                  styles={iconStyle}
                                />
                              }
                            </>
                          )
                      }
                    </div>
                  </div>
                );
              })}
          </div>
          {!this.props.readOnlyMode &&
            <>
              <div>
                <input
                  data-automation-id={AutomationIds.RequestAttachmentInput}
                  className={attachmentsClassNames.uploadButtonInput}
                  type='file'
                  accept={allowedExtns}
                  disabled={attachmentsAvailable <= 0 || this.props.isUploadAttachmentInProgress}
                  ref={this.browseInputRef}
                  onChange={this.onBrowseInputChange}
                />
                <Link
                  data-automation-id={AutomationIds.RequestUploadAttachment}
                  role='button'
                  className={mergeStyles(commonClassNames.textDecorationNone, attachmentsClassNames.uploadButtonLink)}
                  title={getString('attachments_addAFileText')}
                  onClick={this.onAddAFileLinkClick}
                  disabled={attachmentsAvailable <= 0 || this.props.isUploadAttachmentInProgress}
                  hidden={attachmentsAvailable <= 0 || this.props.isUploadAttachmentInProgress}
                  href={'#'}
                >
                  {getString('attachments_addAFile')}
                </Link>
              </div>
              {this.showError() && (
                <div className={attachmentsClassNames.errorMessage}>
                  <span role="alert" tabIndex={0}>
                    {getString(`attachments_ErrorCodes_${ErrorCodes[this.props.attachmentError]}`)}
                  </span>
                </div>
              )}
            </>
          }
        </div>
      </>
    );
  }

  private isErrorSize = (size: number): boolean => {
    const maxFileSizeInMB = this.props.attachmentsConfig.MaxFileSizeInMB;
    const maxFileSizeInB = maxFileSizeInMB * 1024 * 1024;
    let isErrorSize = false;
    if (size > maxFileSizeInB) {
      isErrorSize = true;
      this.props.setAttachmentError(ErrorCodes.AttachmentFileSizeError);
    } else if (size === 0) {
      isErrorSize = true;
      this.props.setAttachmentError(ErrorCodes.AttachmentEmptyFileError);
    }
    return isErrorSize;
  };

  private isErrorFileName = (fileName: string | null): boolean => {
    let isErrorFileName = false;
    const extensionLength = 4;
    if (!fileName || (fileName && fileName.length < extensionLength)) {
      isErrorFileName = true;
      this.props.setAttachmentError(ErrorCodes.AttachmentFileNameError);
    }
    return isErrorFileName;
  };

  private isErrorDuplicatedFile = (fileName: string | null, existingFileNameList: string[]): boolean => {
    let isErrorDuplicatedFile = false;
    if (!!fileName && existingFileNameList.indexOf(fileName) !== -1) {
      isErrorDuplicatedFile = true;
      this.props.setAttachmentError(ErrorCodes.AttachmentDuplicatedFileNameError);
    }
    return isErrorDuplicatedFile;
  };

  private isErrorExtension = (fileName: string | null): boolean => {
    if (!fileName) {
      return true;
    }

    const index = fileName!.lastIndexOf('.');
    if (index < 1) {
      this.props.setAttachmentError(ErrorCodes.AttachmentFileExtensionError);
      return true;
    }

    const ext = fileName.substring(index + 1).toLowerCase();
    const allowedFileExtensions = this.props.attachmentsConfig.AllowedFileExtensions;
    let isErrorExtension = true;
    if (ext) {
      allowedFileExtensions.forEach((data: string) => {
        if (!!ext && data.toLowerCase() === ext.toLowerCase()) {
          isErrorExtension = false;
        }
      });
    }

    if (isErrorExtension) {
      this.props.setAttachmentError(ErrorCodes.AttachmentFileExtensionError);
    }
    return isErrorExtension;
  };

  private uploadAttachment = (doc: File) => {
    const fileNameList =
      this.props.attachments && this.props.attachments.length
        ? this.props.attachments
          .map((attachment: AttachmentData) => {
            return attachment.Name;
          })
        : [];

    if (
      doc &&
      !this.isErrorSize(doc.size) &&
      !this.isErrorFileName(doc.name) &&
      !this.isErrorDuplicatedFile(doc.name, fileNameList) &&
      !this.isErrorExtension(doc.name)
    ) {
      const chunkSizeInMB = this.props.attachmentsConfig.ChunkSizeInMB;
      // Add attachment name first to make it display on UI
      this.props.addAttachmentAction(doc.name, this.props.requestId);

      // Call upload apis
      this.props.dispatchGetUploadAttachmentToken(
          this.props.workspaceId,
          this.props.requestId,
          chunkSizeInMB,
          doc
        );
    }
    this.browseInputRef.current!.value = null;
  };

  private onBrowseInputChange = (_event: React.ChangeEvent<HTMLInputElement>) => {
    this.props.setAttachmentError(null);
    const files = this.browseInputRef.current!.files;
    if (files && files.length > 0) {
      this.uploadAttachment(files[0]);
    }
  };

  private onAddAFileLinkClick = () => {
    this.browseInputRef.current!.click();
  };

  private onDeleteButtonClicked = (cwsId: string) => {
    this.props.deleteAttachmentAction(cwsId, this.props.requestId, this.props.workspaceId);
    this.browseInputRef.current!.value = null;
  };

  private onDownloadButtonClicked = (cwsId: string) => () => {
    this.props.dispatchGetDownloadAttachmentToken(cwsId, this.props.requestId);
  };

  private onCancelButtonClicked = () => {
    this.props.dispatchCancelUploadAttachment(this.props.requestId);
    this.browseInputRef.current!.value = null;
  };

  private showError = (): boolean => {
    return !!this.props.attachmentError;
  };

  private hasAttachments = (): boolean => {
    const attachments = this.props.attachments;
    return attachments && attachments.length > 0;
  };

  private mapAttachmentDataToAttachments = (): Attachment[] => {
    const attachments: Attachment[] = [];
    const docs = this.props.attachments;
    if (docs && docs.length > 0) {
      docs.forEach((doc: AttachmentData) => {
        const attachment = new Attachment();
        attachment.AttachmentData = doc;
        attachment.IsUploadAttachmentInProgress = !attachment.AttachmentData.Location;
        attachments.push(attachment);
      });
    }
    return attachments;
  };
}
